// Shared time-of-day greeting helper.
//
// Used by:
//   • DisplayMode.js — the giant rotating headline on the kitchen display
//   • Dashboard.js   — the calendar-only top heading where TodayPulse is
//                      silent (2+ events) and "What's coming up" felt
//                      like scaffolding text
//
// Three English pools + a mirrored Danish set live here:
//
//   GREETINGS     — mood-dashboard households. Warm "everyone"-style
//                   family copy: "Goodnight everyone", "Morning y'all",
//                   "Lunchtime hello". The household feels addressed.
//
//   GREETINGS_CAL — calendar-only households. Strictly schedule-flavoured:
//                   "Today's plan", "Coming up", "Tomorrow's view".
//                   Never check-ins, never moods.
//
//   GREETINGS_CAL_EMPTY — calendar-only households on days with NOTHING
//                   left on the books. Calm/soft; never "Today continues"
//                   or anything that contradicts the "Nothing else on" body.
//
// The picker is deterministic per (day-of-year × band) so the same band
// on the same day always renders the same line — no flicker as minutes
// tick over. Hour bands match what felt right on the kitchen iPad:
//   night     22:00 – 04:59
//   morning   05:00 – 11:59
//   midday    12:00 – 13:59
//   afternoon 14:00 – 16:59
//   evening   17:00 – 21:59
//
// LANGUAGE: The picker takes a `lang` argument (`"en"` default, `"da"` for
// Danish). Danish pools are CURATED — hand-picked warm phrases matching
// the editorial tone, not literal Google-translate renderings. If a locale
// key doesn't exist we fall through to English, so callers can safely
// pass any string.

const GREETINGS_EN = {
  night: [
    "Goodnight everyone",
    "Night everyone",
    "Sleep well everyone",
    "Rest well tonight",
    "Time to wind down",
    "Night night everyone",
    "Sweet dreams",
    "Time for bed soon",
    "Sweet dreams everyone",
    "Quiet night ahead",
  ],
  late_night: [
    "Still up?",
    "Late night vibes",
    "Quiet hours",
    "Burning the midnight oil",
    "House is asleep",
    "After-hours check-in",
    "Soft hours",
    "Hush time",
    "Late and lovely",
    "Whispered hours",
  ],
  morning: [
    "Morning y'all",
    "Good morning everyone",
    "Morning everyone",
    "Rise and shine",
    "Hope you slept well",
    "New day, fresh start",
    "Morning vibes",
    "Ready for the day?",
    "Hope today treats you well",
    "Let's ease into the day",
  ],
  midday: [
    "Hello guys",
    "Hey there",
    "Hi everyone",
    "Midday check-in?",
    "Hope your day's going well",
    "Lunchtime hello",
    "Quick hello",
    "How's your day so far?",
    "Midday vibes",
  ],
  afternoon: [
    "Good afternoon everyone",
    "Afternoon vibes",
    "Afternoon everyone",
    "Hope your afternoon's good",
    "Still going strong?",
    "Afternoon check-in",
    "Keep it going y'all",
    "Hope you're all well",
    "Smooth afternoon",
    "Mid-afternoon vibes",
  ],
  evening: [
    "Good evening everyone",
    "Evening family",
    "Evening everyone",
    "Evening feels",
    "Evening vibes",
    "Evening check-in",
    "Time to slow down",
    "How's your evening going?",
    "Evening time",
    "End of day vibes",
  ],
};

const GREETINGS_CAL_EN = {
  night: [
    "Tomorrow's view", "Night before", "Closing the day", "Looking ahead",
    "Quiet evening", "Last look", "Settling in", "Day's end",
    "Resting easy", "Calendar's calm",
  ],
  late_night: [
    "Late hours", "Quiet hours", "After midnight", "Still up",
    "Small hours", "Hush", "House asleep", "Soft hours",
    "Late check-in", "Calendar at rest",
  ],
  morning: [
    "Good morning", "Day ahead", "Morning view", "Morning vibes",
    "Today's plan", "Today at a glance", "Coming up", "On the books",
    "What's on today", "Fresh page",
  ],
  midday: [
    "Midday view", "Half-day in", "What's left of today", "Today still",
    "Day in motion", "Midday plan", "Glance ahead", "Today continues",
    "Afternoon coming", "Still on the books",
  ],
  afternoon: [
    "Afternoon view", "Afternoon plan", "Day ahead", "Wrapping the day",
    "Looking forward", "Coming up next", "Still ahead", "Afternoon glance",
    "Day continues", "On the calendar",
  ],
  evening: [
    "Evening view", "Tomorrow ahead", "Looking ahead", "Day winding down",
    "Coming up next", "On tomorrow", "Tomorrow's plan", "End-of-day view",
    "Evening glance", "What's next",
  ],
};

const GREETINGS_CAL_EMPTY_EN = {
  night: [
    "A quiet night", "All settled", "Kitchen's closed", "Day is done",
    "Curtain down", "Wrapped up early", "Empty evening", "A still night",
    "Soft hours", "No more on the books",
  ],
  late_night: [
    "All quiet", "Hush", "House asleep", "Soft hours",
    "Nothing pending", "Stillness", "Calm hours", "Late and clear",
    "Empty diary", "Day already done",
  ],
  morning: [
    "A clear morning", "Empty diary", "A free day", "Nothing on the books",
    "A wide-open day", "An easy morning", "Blank page today", "A still morning",
    "Open hours", "All yours today",
  ],
  midday: [
    "A blank afternoon", "Open afternoon", "Quiet midday", "Free hours",
    "Empty diary", "Nothing pressing", "An easy afternoon", "Open day still",
    "Soft middle", "All clear",
  ],
  afternoon: [
    "An easy afternoon", "Open hours", "A free afternoon", "Quiet day",
    "Nothing else booked", "Soft afternoon", "Slow hours", "A blank stretch",
    "Empty diary", "Slack day",
  ],
  evening: [
    "An empty evening", "Quiet hours", "Nothing else booked", "A slow night",
    "Blank evening", "Soft evening", "All settled", "Calm hours",
    "Day is done", "An easy evening",
  ],
};

// ── Danish pools ──────────────────────────────────────────────────
// Curated by user 2026-07-18. Danish families see ONE unified pool
// across mood-mode, calendar-mode and calendar-empty — the English
// side has three specialised pools, but in Danish the tone doesn't
// need to differ between "dashboard says nothing scheduled" and
// "dashboard shows two events". Simpler + more coherent.
const GREETINGS_DA = {
  night: [
    "Godnat alle sammen",
    "Sov godt",
    "Godnat",
    "— tid til at sove",
    "Godnat familie",
    "Søde drømme",
    "Snart i seng",
    "Rolig aften",
  ],
  late_night: [
    "Stadig oppe?",
    "Sene timer",
    "Stille i huset",
    "Nattevagtsstemning",
    "Alle andre sover",
    "Nat nat",
    "Sent og fint",
    "Hviskende timer",
  ],
  morning: [
    "Godmorgen alle sammen",
    "Godmorgen",
    "Op med solen",
    "Håber I sov godt",
    "Ny dag, frisk start",
    "Morgenvibes",
    "Klar til dagen?",
    "God dag forude",
    "Godmorgen familie",
  ],
  midday: [
    "Hej alle sammen",
    "Hej med jer",
    "Middagshilsen",
    "Frokosthilsen",
    "Hvordan går dagen?",
    "Kort hej",
    "Midt-på-dagen check-in",
    "Håber dagen behandler jer godt",
  ],
  afternoon: [
    "Lækker eftermiddag",
    "God eftermiddag",
    "Eftermiddagsvibes",
    "Håber eftermiddagen er god",
    "Stadig i gang?",
    "Eftermiddags-hilsen",
    "Hold den kørende",
    "Håber I har det godt",
    "Rolig eftermiddag",
    "Sen eftermiddag",
  ],
  evening: [
    "God aften alle sammen",
    "God aften",
    "Aftenvibes",
    "Aften-hilsen",
    "Tid til at slappe af",
    "Hvordan går aftenen?",
    "Aften-tid",
    "Så er dagen ved at være omme",
    "Slap-af-tid",
    "Tid til at geare ned",
  ],
};

const POOLS = {
  en: {
    default: GREETINGS_EN,
    calendar: GREETINGS_CAL_EN,
    calendar_empty: GREETINGS_CAL_EMPTY_EN,
  },
  // Danish uses a single unified pool for all three buckets — the
  // English side splits mood/calendar/calendar-empty into different
  // registers, but the Danish family-radio tone works the same in
  // every context, so mood-mode families and calendar-mode families
  // and calendar-empty days all draw from the same GREETINGS_DA pool.
  da: {
    default: GREETINGS_DA,
    calendar: GREETINGS_DA,
    calendar_empty: GREETINGS_DA,
  },
};

export const bandFor = (hour) => {
  // Night splits into two sub-bands so post-midnight late nights
  // ("you're still up at 02:00") don't get tomorrow-claiming
  // headlines that contradict the rest of the UI showing today's
  // date + "TODAY · 16:00" event labels. Pre-midnight late evening
  // = "tomorrow really IS tomorrow" works; after midnight we're
  // already on the new calendar day so "tomorrow" actually means
  // the day AFTER today, which is two days from when we last slept.
  if (hour < 5) return "late_night";
  if (hour < 12) return "morning";
  if (hour < 14) return "midday";
  if (hour < 17) return "afternoon";
  if (hour < 22) return "evening";
  return "night";
};

const BANDS = ["night", "late_night", "morning", "midday", "afternoon", "evening"];

export function greetingFor(
  date,
  { calendarOnly = false, eventsLeftToday = null, lang = "en" } = {},
) {
  const band = bandFor(date.getHours());
  // When the caller knows there's literally nothing else on today AND
  // we're in the calendar context, pick from the calm "empty" pool so
  // the H1 reads honestly with TodayPulse's "Nothing else on" body
  // instead of contradicting it. `null` keeps the legacy behaviour
  // for callers that don't know the count yet (DisplayMode etc).
  const useEmpty =
    calendarOnly && typeof eventsLeftToday === "number" && eventsLeftToday === 0;
  const bucket = calendarOnly
    ? (useEmpty ? "calendar_empty" : "calendar")
    : "default";

  // Fall through EN if a locale-key is missing, so unsupported locales
  // never crash the H1.
  const langPools = POOLS[lang] || POOLS.en;
  const root = langPools[bucket] || POOLS.en[bucket];
  const pool = root[band] || POOLS.en[bucket][band];

  const dayOfYear = Math.floor(
    (date - new Date(date.getFullYear(), 0, 0)) / 86400000
  );
  const bandIdx = BANDS.indexOf(band);
  const i = (dayOfYear * 5 + bandIdx) % pool.length;
  return pool[i];
}
